import React from 'react';
import { Icon } from '../general';
import { IconName } from '../../assets/icons';
import { DOCK_DEFAULT_ICON_SIZE } from '../../constants/desktopConfig';

export interface DockApp {
    key: string;
    name: string;
    icon: IconName;
    iconStyle: 'apple' | 'retro';
    iconSize?: number;
    isSeparator?: boolean;
}

export interface ToolbarProps {
    dockApps: DockApp[];
    windows: DesktopWindows;
    onOpenApp: (key: string) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
    dockApps,
    windows,
    onOpenApp,
}) => {
    return (
        <div style={styles.dockOuter}>
            <div style={styles.dock}>
                {dockApps.map((app) => {
                    if (app.isSeparator) {
                        return <div key={app.key} style={styles.dockSeparator} />;
                    }
                    const isRunning = !!windows[app.key];
                    const isMinimized = windows[app.key]?.minimized;
                    const iconPx = app.iconSize ?? DOCK_DEFAULT_ICON_SIZE;
                    return (
                        <div
                            key={app.key}
                            style={styles.dockItem}
                            onMouseDown={() => onOpenApp(app.key)}
                            title={app.name}
                        >
                            <div style={styles.dockIconWrap}>
                                <Icon
                                    icon={app.icon}
                                    size={iconPx}
                                    smooth={app.iconStyle === 'apple'}
                                    style={Object.assign({}, styles.dockIcon, { width: iconPx, height: iconPx })}
                                />
                                {isRunning && (
                                    <div
                                        style={Object.assign(
                                            {},
                                            styles.runningDot,
                                            isMinimized && styles.runningDotMinimized
                                        )}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const styles: StyleSheetCSS = {
    dockOuter: {
        position: 'fixed',
        bottom: 12,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 100000,
        pointerEvents: 'none',
    },
    dock: {
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        background: 'rgba(45, 45, 52, 0.72)',
        backdropFilter: 'blur(24px) saturate(110%)',
        WebkitBackdropFilter: 'blur(24px) saturate(110%)',
        borderRadius: 14,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: 'none',
        isolation: 'isolate',
        transform: 'translateZ(0)',
    },
    dockItem: {
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    dockSeparator: {
        width: 1,
        height: 44,
        marginLeft: 8,
        marginRight: 4,
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: 1,
    },
    dockIconWrap: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dockIcon: {
        width: 52,
        height: 52,
    },
    runningDot: {
        position: 'absolute',
        bottom: -6,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 4,
        height: 4,
        borderRadius: 2,
        background: 'rgba(255, 255, 255, 0.95)',
    },
    runningDotMinimized: {
        opacity: 0.6,
    },
};

export default Toolbar;
